import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowRight, UtensilsCrossed, Printer, Copy, Share2, Mail, RefreshCw, ChevronDown, ChevronUp, ShoppingCart, Lightbulb, Check, Dumbbell } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { trackEvent } from '../utils/analytics';

// Expanded meal database with variety
const MEAL_DATABASE = {
  standard: {
    breakfasts: [
      { name: 'Greek yogurt parfait with berries and honey', protein: 20, calories: 280 },
      { name: '2-egg veggie scramble with spinach, mushrooms, and feta', protein: 18, calories: 250 },
      { name: 'Cottage cheese bowl with cucumber, tomatoes, everything seasoning', protein: 22, calories: 220 },
      { name: 'Protein smoothie — banana, protein powder, almond milk, spinach, PB', protein: 30, calories: 320 },
      { name: 'Turkey and egg white breakfast wrap in low-carb tortilla', protein: 28, calories: 280 },
      { name: 'Overnight oats with chia seeds, almond milk, and berries', protein: 15, calories: 300 },
      { name: 'Smoked salmon on cucumber slices with cream cheese', protein: 22, calories: 240 },
      { name: 'Avocado toast on whole grain with 2 poached eggs', protein: 18, calories: 350 },
      { name: 'Scrambled eggs with turkey sausage', protein: 26, calories: 300 },
      { name: 'Greek yogurt with granola and sliced almonds', protein: 22, calories: 320 }
    ],
    lunches: [
      { name: 'Grilled chicken Caesar salad (light dressing)', protein: 35, calories: 380 },
      { name: 'Turkey and avocado lettuce wrap with mustard', protein: 28, calories: 280 },
      { name: 'Tuna salad over mixed greens with olive oil', protein: 32, calories: 320 },
      { name: 'Lentil soup with a side of sliced turkey', protein: 28, calories: 340 },
      { name: 'Greek bowl — chicken, cucumber, tomato, olives, feta, tzatziki', protein: 34, calories: 400 },
      { name: 'Shrimp stir fry with zucchini noodles and soy sauce', protein: 30, calories: 280 },
      { name: 'Cottage cheese and veggie stuffed bell pepper', protein: 24, calories: 260 },
      { name: 'Chicken and black bean burrito bowl (no rice)', protein: 36, calories: 380 },
      { name: 'Grilled salmon with quinoa and spinach', protein: 38, calories: 420 },
      { name: 'Turkey lettuce wraps with hummus', protein: 26, calories: 260 }
    ],
    dinners: [
      { name: 'Baked salmon with roasted asparagus and quinoa', protein: 38, calories: 450 },
      { name: 'Ground turkey taco bowl with cauliflower rice, salsa, Greek yogurt', protein: 34, calories: 380 },
      { name: 'Lemon herb chicken thighs with roasted Brussels sprouts', protein: 36, calories: 400 },
      { name: 'Shrimp and veggie skewers with a side salad', protein: 30, calories: 320 },
      { name: 'Turkey meatballs with zucchini noodles and marinara', protein: 32, calories: 360 },
      { name: 'Baked cod with steamed broccoli and sweet potato', protein: 34, calories: 380 },
      { name: 'Stuffed portobello mushrooms with ground turkey and cheese', protein: 30, calories: 340 },
      { name: 'Chicken soup with vegetables and barley', protein: 28, calories: 320 },
      { name: 'Grilled chicken breast with roasted vegetables', protein: 40, calories: 400 },
      { name: 'Lean beef stir-fry with bell peppers and brown rice', protein: 36, calories: 420 }
    ],
    snacks: [
      { name: 'Hard boiled eggs (2)', protein: 12, calories: 140 },
      { name: 'String cheese and apple slices', protein: 8, calories: 160 },
      { name: 'Edamame (1 cup)', protein: 11, calories: 120 },
      { name: 'Hummus with cucumber and bell pepper strips', protein: 6, calories: 150 },
      { name: 'Greek yogurt (plain)', protein: 15, calories: 100 },
      { name: 'Handful of almonds with a piece of fruit', protein: 8, calories: 180 },
      { name: 'Protein bar (RX Bar or Quest)', protein: 20, calories: 200 },
      { name: 'Cottage cheese with hot sauce', protein: 14, calories: 120 },
      { name: 'Turkey roll-ups with mustard', protein: 15, calories: 100 }
    ]
  },
  lowcarb: {
    breakfasts: [
      { name: '2-egg veggie scramble with spinach, mushrooms, and feta', protein: 18, calories: 250 },
      { name: 'Cottage cheese bowl with cucumber, tomatoes, everything seasoning', protein: 22, calories: 220 },
      { name: 'Smoked salmon on cucumber slices with cream cheese', protein: 22, calories: 240 },
      { name: 'Bacon and eggs with avocado', protein: 24, calories: 350 },
      { name: 'Turkey and egg white breakfast wrap in low-carb tortilla', protein: 28, calories: 280 },
      { name: 'Egg muffins with cheese and vegetables', protein: 20, calories: 220 },
      { name: 'Greek yogurt with nuts (no fruit)', protein: 18, calories: 200 }
    ],
    lunches: [
      { name: 'Grilled chicken Caesar salad (light dressing)', protein: 35, calories: 380 },
      { name: 'Turkey and avocado lettuce wrap with mustard', protein: 28, calories: 280 },
      { name: 'Tuna salad over mixed greens with olive oil', protein: 32, calories: 320 },
      { name: 'Bunless burger with cheese and side salad', protein: 35, calories: 400 },
      { name: 'Shrimp stir fry with zucchini noodles and soy sauce', protein: 30, calories: 280 },
      { name: 'Cobb salad with ranch dressing', protein: 32, calories: 420 },
      { name: 'Chicken thighs with roasted vegetables', protein: 34, calories: 380 }
    ],
    dinners: [
      { name: 'Ground turkey taco bowl with cauliflower rice, salsa, Greek yogurt', protein: 34, calories: 380 },
      { name: 'Lemon herb chicken thighs with roasted Brussels sprouts', protein: 36, calories: 400 },
      { name: 'Shrimp and veggie skewers with a side salad', protein: 30, calories: 320 },
      { name: 'Turkey meatballs with zucchini noodles and marinara', protein: 32, calories: 360 },
      { name: 'Stuffed portobello mushrooms with ground turkey and cheese', protein: 30, calories: 340 },
      { name: 'Ribeye steak with asparagus', protein: 42, calories: 500 },
      { name: 'Salmon with garlic butter and green beans', protein: 36, calories: 420 }
    ],
    snacks: [
      { name: 'Hard boiled eggs (2)', protein: 12, calories: 140 },
      { name: 'String cheese', protein: 7, calories: 80 },
      { name: 'Edamame (1 cup)', protein: 11, calories: 120 },
      { name: 'Celery with almond butter', protein: 6, calories: 150 },
      { name: 'Beef jerky', protein: 14, calories: 100 },
      { name: 'Cottage cheese with hot sauce', protein: 14, calories: 120 },
      { name: 'Turkey roll-ups with mustard', protein: 15, calories: 100 }
    ]
  },
  highprotein: {
    breakfasts: [
      { name: 'Protein smoothie — banana, protein powder, almond milk, spinach, PB', protein: 40, calories: 380 },
      { name: 'Turkey and egg white breakfast wrap (double protein)', protein: 38, calories: 320 },
      { name: '4-egg scramble with turkey sausage', protein: 36, calories: 380 },
      { name: 'Greek yogurt parfait with protein powder added', protein: 35, calories: 300 },
      { name: 'Cottage cheese bowl with smoked salmon', protein: 34, calories: 280 },
      { name: 'Egg white omelet with chicken and cheese', protein: 40, calories: 300 },
      { name: 'Protein pancakes with Greek yogurt', protein: 35, calories: 350 }
    ],
    lunches: [
      { name: 'Double chicken breast salad with olive oil', protein: 50, calories: 450 },
      { name: 'Tuna steak with mixed greens', protein: 45, calories: 380 },
      { name: 'Greek bowl with double chicken', protein: 48, calories: 480 },
      { name: 'Turkey and egg white wrap with extra turkey', protein: 42, calories: 350 },
      { name: 'Shrimp and chicken combo stir fry', protein: 48, calories: 400 },
      { name: 'Salmon with cottage cheese side', protein: 44, calories: 420 },
      { name: 'Lean beef patties (2) with side salad', protein: 46, calories: 450 }
    ],
    dinners: [
      { name: 'Double salmon filets with roasted vegetables', protein: 55, calories: 520 },
      { name: 'Chicken breast duo with sweet potato', protein: 52, calories: 480 },
      { name: 'Lean steak with egg and broccoli', protein: 50, calories: 500 },
      { name: 'Turkey breast with quinoa and vegetables', protein: 48, calories: 450 },
      { name: 'Shrimp and scallop plate with asparagus', protein: 45, calories: 380 },
      { name: 'Ground turkey bowl with black beans', protein: 48, calories: 420 },
      { name: 'Baked cod with lentils', protein: 46, calories: 400 }
    ],
    snacks: [
      { name: 'Protein shake', protein: 30, calories: 160 },
      { name: 'Greek yogurt with protein powder', protein: 35, calories: 180 },
      { name: 'Chicken breast strips (4oz)', protein: 28, calories: 140 },
      { name: 'Tuna packet', protein: 25, calories: 100 },
      { name: 'Cottage cheese (1 cup)', protein: 25, calories: 160 },
      { name: 'Egg whites (6)', protein: 24, calories: 100 },
      { name: 'High protein bar', protein: 30, calories: 220 }
    ]
  },
  vegetarian: {
    breakfasts: [
      { name: 'Greek yogurt parfait with berries and honey', protein: 20, calories: 280 },
      { name: '2-egg veggie scramble with spinach, mushrooms, and feta', protein: 18, calories: 250 },
      { name: 'Cottage cheese bowl with cucumber, tomatoes, everything seasoning', protein: 22, calories: 220 },
      { name: 'Protein smoothie with plant protein powder', protein: 25, calories: 300 },
      { name: 'Overnight oats with chia seeds and almond butter', protein: 18, calories: 340 },
      { name: 'Tofu scramble with vegetables', protein: 18, calories: 220 },
      { name: 'Avocado toast with 2 poached eggs', protein: 18, calories: 350 }
    ],
    lunches: [
      { name: 'Lentil soup with whole grain bread', protein: 22, calories: 380 },
      { name: 'Greek salad with chickpeas and feta', protein: 18, calories: 340 },
      { name: 'Black bean burrito bowl with Greek yogurt', protein: 20, calories: 400 },
      { name: 'Caprese salad with quinoa', protein: 16, calories: 360 },
      { name: 'Tofu stir fry with brown rice', protein: 22, calories: 380 },
      { name: 'Egg salad sandwich on whole grain', protein: 18, calories: 380 },
      { name: 'Cottage cheese and veggie stuffed bell pepper', protein: 24, calories: 260 }
    ],
    dinners: [
      { name: 'Vegetable curry with tofu over rice', protein: 24, calories: 420 },
      { name: 'Black bean tacos with Greek yogurt crema', protein: 22, calories: 400 },
      { name: 'Eggplant parmesan with side salad', protein: 18, calories: 380 },
      { name: 'Lentil pasta with vegetables', protein: 24, calories: 450 },
      { name: 'Stuffed peppers with quinoa and cheese', protein: 20, calories: 360 },
      { name: 'Tempeh stir fry with vegetables', protein: 26, calories: 380 },
      { name: 'Paneer tikka with rice and vegetables', protein: 24, calories: 440 }
    ],
    snacks: [
      { name: 'Hard boiled eggs (2)', protein: 12, calories: 140 },
      { name: 'Hummus with cucumber and bell pepper strips', protein: 6, calories: 150 },
      { name: 'Greek yogurt (plain)', protein: 15, calories: 100 },
      { name: 'Edamame (1 cup)', protein: 11, calories: 120 },
      { name: 'Cottage cheese with berries', protein: 14, calories: 140 },
      { name: 'Trail mix with nuts and seeds', protein: 8, calories: 200 },
      { name: 'String cheese with apple', protein: 8, calories: 160 }
    ]
  }
};

// Nausea-friendly meals (bland, easy to digest)
const NAUSEA_MEALS = {
  breakfasts: [
    { name: 'Plain toast with light butter', protein: 4, calories: 120 },
    { name: 'Banana with small portion plain yogurt', protein: 8, calories: 150 },
    { name: 'Plain oatmeal with honey', protein: 5, calories: 180 },
    { name: 'Scrambled eggs (plain, no seasoning)', protein: 12, calories: 140 },
    { name: 'Rice porridge with a soft boiled egg', protein: 10, calories: 200 },
    { name: 'Applesauce with plain crackers', protein: 2, calories: 140 },
    { name: 'Plain Greek yogurt (room temperature)', protein: 15, calories: 100 }
  ],
  lunches: [
    { name: 'Plain crackers with tuna (no mayo)', protein: 22, calories: 200 },
    { name: 'Plain rice with baked chicken breast', protein: 30, calories: 320 },
    { name: 'Chicken noodle soup (broth-based)', protein: 18, calories: 220 },
    { name: 'Plain baked potato with cottage cheese', protein: 18, calories: 280 },
    { name: 'Steamed fish with white rice', protein: 28, calories: 300 },
    { name: 'Turkey sandwich on white bread (no sauce)', protein: 22, calories: 280 },
    { name: 'Plain pasta with light olive oil', protein: 8, calories: 260 }
  ],
  dinners: [
    { name: 'Baked chicken with mashed potatoes (no butter)', protein: 35, calories: 380 },
    { name: 'Steamed tilapia with plain rice', protein: 30, calories: 320 },
    { name: 'Turkey breast with steamed vegetables', protein: 34, calories: 340 },
    { name: 'Plain pasta with grilled chicken strips', protein: 32, calories: 380 },
    { name: 'Baked cod with steamed carrots', protein: 28, calories: 280 },
    { name: 'Chicken and rice soup', protein: 26, calories: 300 },
    { name: 'Ground turkey with plain sweet potato', protein: 30, calories: 350 }
  ],
  snacks: [
    { name: 'Saltine crackers (6)', protein: 2, calories: 80 },
    { name: 'Ginger chews or ginger tea', protein: 0, calories: 40 },
    { name: 'Plain rice cakes', protein: 2, calories: 70 },
    { name: 'Banana', protein: 1, calories: 100 },
    { name: 'Cold cottage cheese', protein: 14, calories: 120 },
    { name: 'Hard boiled eggs (cold)', protein: 12, calories: 140 },
    { name: 'Peppermint tea with crackers', protein: 2, calories: 60 }
  ]
};

// Low appetite meals (under 400 cal, high protein density)
const LOW_APPETITE_MEALS = {
  breakfasts: [
    { name: 'Protein shake (simple, not too filling)', protein: 30, calories: 180 },
    { name: 'Greek yogurt with protein powder', protein: 35, calories: 200 },
    { name: 'Cottage cheese (1 cup)', protein: 25, calories: 180 },
    { name: '2 hard boiled eggs', protein: 12, calories: 140 },
    { name: 'Small protein smoothie', protein: 25, calories: 200 },
    { name: 'Egg whites with cheese (4 whites)', protein: 18, calories: 120 },
    { name: 'Greek yogurt with almonds', protein: 20, calories: 180 }
  ],
  lunches: [
    { name: 'Tuna packet with crackers', protein: 25, calories: 200 },
    { name: 'Deli turkey roll-ups (4oz)', protein: 24, calories: 140 },
    { name: 'Cottage cheese with cucumber', protein: 22, calories: 160 },
    { name: 'Chicken breast strips (4oz)', protein: 28, calories: 150 },
    { name: 'Greek yogurt with sliced turkey', protein: 30, calories: 200 },
    { name: 'Protein bar with string cheese', protein: 28, calories: 280 },
    { name: 'Shrimp cocktail (6oz)', protein: 24, calories: 140 }
  ],
  dinners: [
    { name: 'Baked chicken breast (6oz) with side salad', protein: 42, calories: 280 },
    { name: 'Grilled salmon (5oz) with steamed broccoli', protein: 35, calories: 300 },
    { name: 'Turkey meatballs (5) with zucchini', protein: 30, calories: 280 },
    { name: 'Shrimp stir fry (light portion)', protein: 28, calories: 250 },
    { name: 'Baked cod (6oz) with asparagus', protein: 32, calories: 240 },
    { name: 'Lean ground turkey bowl (4oz)', protein: 28, calories: 260 },
    { name: 'Egg white omelet with vegetables', protein: 24, calories: 180 }
  ],
  snacks: [
    { name: 'Protein shake (small)', protein: 25, calories: 140 },
    { name: 'Greek yogurt', protein: 15, calories: 100 },
    { name: 'Hard boiled egg', protein: 6, calories: 70 },
    { name: 'Turkey roll-up', protein: 8, calories: 50 },
    { name: 'Cottage cheese (half cup)', protein: 12, calories: 80 },
    { name: 'String cheese', protein: 7, calories: 80 },
    { name: 'Beef jerky (1oz)', protein: 10, calories: 80 }
  ]
};

// Energy-focused meals (with complex carbs)
const ENERGY_MEALS = {
  breakfasts: [
    { name: 'Oatmeal with banana, protein powder, and almond butter', protein: 25, calories: 420 },
    { name: 'Whole grain toast with eggs and avocado', protein: 20, calories: 400 },
    { name: 'Sweet potato hash with eggs and turkey sausage', protein: 26, calories: 420 },
    { name: 'Overnight oats with Greek yogurt and berries', protein: 22, calories: 380 },
    { name: 'Quinoa breakfast bowl with eggs and vegetables', protein: 24, calories: 400 },
    { name: 'Whole grain pancakes with protein powder', protein: 28, calories: 420 },
    { name: 'Brown rice porridge with eggs and spinach', protein: 20, calories: 380 }
  ],
  lunches: [
    { name: 'Chicken breast with quinoa and roasted vegetables', protein: 40, calories: 480 },
    { name: 'Turkey sandwich on whole grain with sweet potato', protein: 32, calories: 500 },
    { name: 'Salmon with brown rice and broccoli', protein: 38, calories: 480 },
    { name: 'Lentil soup with whole grain bread and chicken', protein: 35, calories: 460 },
    { name: 'Burrito bowl with brown rice, beans, and chicken', protein: 38, calories: 520 },
    { name: 'Tuna with whole wheat pasta and vegetables', protein: 36, calories: 480 },
    { name: 'Greek bowl with chicken, quinoa, and hummus', protein: 38, calories: 500 }
  ],
  dinners: [
    { name: 'Grilled salmon with sweet potato and asparagus', protein: 40, calories: 500 },
    { name: 'Chicken thighs with brown rice and Brussels sprouts', protein: 38, calories: 520 },
    { name: 'Lean beef stir fry with brown rice and vegetables', protein: 36, calories: 500 },
    { name: 'Turkey meatballs with whole wheat pasta', protein: 34, calories: 480 },
    { name: 'Baked cod with quinoa and roasted vegetables', protein: 36, calories: 460 },
    { name: 'Shrimp with barley and mixed vegetables', protein: 32, calories: 440 },
    { name: 'Chicken soup with barley and vegetables', protein: 30, calories: 400 }
  ],
  snacks: [
    { name: 'Apple with almond butter', protein: 6, calories: 200 },
    { name: 'Trail mix with nuts and dried fruit', protein: 8, calories: 220 },
    { name: 'Whole grain crackers with hummus', protein: 6, calories: 180 },
    { name: 'Banana with protein shake', protein: 28, calories: 280 },
    { name: 'Oat bar with Greek yogurt', protein: 18, calories: 260 },
    { name: 'Sweet potato bites with cottage cheese', protein: 16, calories: 200 },
    { name: 'Fruit and nut energy bites', protein: 8, calories: 180 }
  ]
};

// Simple consistency meals (5 ingredients or less, under 10 min prep)
const CONSISTENCY_MEALS = {
  breakfasts: [
    { name: 'Greek yogurt + berries + honey', protein: 18, calories: 220 },
    { name: '2 hard boiled eggs + toast', protein: 14, calories: 220 },
    { name: 'Protein shake + banana', protein: 28, calories: 280 },
    { name: 'Cottage cheese + fruit', protein: 22, calories: 200 },
    { name: 'Overnight oats (prep night before)', protein: 15, calories: 300 },
    { name: 'Scrambled eggs + cheese', protein: 18, calories: 240 },
    { name: 'Greek yogurt + granola', protein: 20, calories: 280 }
  ],
  lunches: [
    { name: 'Rotisserie chicken + bagged salad + dressing', protein: 35, calories: 380 },
    { name: 'Canned tuna + crackers + fruit', protein: 25, calories: 280 },
    { name: 'Deli turkey + cheese + bread', protein: 28, calories: 340 },
    { name: 'Pre-made soup + string cheese', protein: 20, calories: 280 },
    { name: 'Rotisserie chicken + pre-cut veggies', protein: 32, calories: 300 },
    { name: 'Cottage cheese + deli meat + crackers', protein: 30, calories: 300 },
    { name: 'Pre-made protein bowl (grocery store)', protein: 28, calories: 400 }
  ],
  dinners: [
    { name: 'Rotisserie chicken + microwave rice + frozen veggies', protein: 38, calories: 450 },
    { name: 'Frozen salmon filet + bagged salad', protein: 35, calories: 380 },
    { name: 'Ground turkey + jarred sauce + zucchini', protein: 32, calories: 360 },
    { name: 'Pre-marinated chicken + frozen stir fry veggies', protein: 36, calories: 380 },
    { name: 'Shrimp (frozen) + pre-made rice + broccoli', protein: 30, calories: 360 },
    { name: 'Rotisserie chicken + sweet potato + butter', protein: 35, calories: 420 },
    { name: 'Frozen fish + microwave veggies + lemon', protein: 30, calories: 300 }
  ],
  snacks: [
    { name: 'String cheese + apple', protein: 8, calories: 160 },
    { name: 'Protein bar', protein: 20, calories: 200 },
    { name: 'Greek yogurt cup', protein: 15, calories: 120 },
    { name: 'Hard boiled eggs (store bought)', protein: 12, calories: 140 },
    { name: 'Nuts + piece of fruit', protein: 8, calories: 180 },
    { name: 'Cottage cheese cup', protein: 14, calories: 120 },
    { name: 'Turkey jerky', protein: 12, calories: 100 }
  ]
};

// Challenge-specific notes
const CHALLENGE_NOTES = {
  nausea: "These meals are specifically chosen to minimize nausea — smaller portions more frequently works better than 3 full meals. Stick to room temperature or cold foods, and avoid heavy seasonings in weeks 1-2.",
  lowappetite: "When appetite is low, lead with protein first — even a few bites counts. Every meal here is under 400 calories but packed with protein to maximize nutrition in smaller portions.",
  lowenergy: "Don't cut carbs completely — complex carbs give you sustained energy throughout the day. We've included sweet potato, oats, quinoa, or brown rice at every meal.",
  consistency: "Every meal here takes under 10 minutes — consistency beats perfection. Focus on showing up daily, not cooking elaborate meals."
};

// Meal prep tips by challenge
const MEAL_PREP_TIPS = {
  nausea: [
    "Cook chicken plain without heavy seasoning this week — spices can worsen nausea early on",
    "Keep ginger chews or ginger tea on hand for breakthrough nausea",
    "Prepare foods the night before and refrigerate — cold foods are often better tolerated",
    "Eat small amounts every 2-3 hours rather than 3 big meals"
  ],
  lowappetite: [
    "Pre-portion proteins into small containers so eating feels less overwhelming",
    "Keep protein shakes ready in the fridge for days when solid food is hard",
    "Eat your protein first at every meal before you feel full",
    "Set phone reminders to eat — don't wait for hunger signals"
  ],
  lowenergy: [
    "Batch cook sweet potatoes and quinoa Sunday — they reheat perfectly all week",
    "Prep overnight oats for 5 days in mason jars",
    "Cook a large batch of brown rice to use across multiple meals",
    "Pre-chop vegetables so adding them to meals takes seconds"
  ],
  consistency: [
    "Buy rotisserie chicken — it works in 5 different meals and requires zero cooking",
    "Stock up on frozen vegetables and microwave rice pouches",
    "Keep protein bars in your car, desk, and bag for backup meals",
    "Use the same simple meals on repeat — variety is overrated when building habits"
  ]
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Shuffle array helper
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const MealPlan = () => {
  const navigate = useNavigate();
  const [medication, setMedication] = useState('');
  const [diet, setDiet] = useState('');
  const [challenge, setChallenge] = useState('');
  const [activity, setActivity] = useState('');
  const [currentWeight, setCurrentWeight] = useState('');
  const [goalWeight, setGoalWeight] = useState('');
  const [showPlan, setShowPlan] = useState(false);
  const [expandedDays, setExpandedDays] = useState({ Monday: true, Tuesday: true });
  const [mealPlan, setMealPlan] = useState(null);
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const generateMealPlan = () => {
    trackEvent('meal_plan_generated', { medication, diet, challenge, activity });
    const plan = createMealPlan();
    setMealPlan(plan);
    setShowPlan(true);
  };

  const regeneratePlan = () => {
    trackEvent('meal_plan_regenerated', { medication, diet, challenge, activity });
    const plan = createMealPlan();
    setMealPlan(plan);
  };

  const createMealPlan = () => {
    // Select meal source based on challenge
    let mealSource;
    if (challenge === 'nausea') {
      mealSource = NAUSEA_MEALS;
    } else if (challenge === 'lowappetite') {
      mealSource = LOW_APPETITE_MEALS;
    } else if (challenge === 'lowenergy') {
      mealSource = ENERGY_MEALS;
    } else if (challenge === 'consistency') {
      mealSource = CONSISTENCY_MEALS;
    } else {
      // Use diet-based meals
      const dietKey = diet === 'lowcarb' ? 'lowcarb' : 
                      diet === 'highprotein' ? 'highprotein' :
                      diet === 'vegetarian' ? 'vegetarian' : 'standard';
      mealSource = MEAL_DATABASE[dietKey];
    }

    // Shuffle meals for variety
    const breakfasts = shuffleArray(mealSource.breakfasts);
    const lunches = shuffleArray(mealSource.lunches);
    const dinners = shuffleArray(mealSource.dinners);
    const snacks = shuffleArray(mealSource.snacks);

    // Calculate protein target
    const weight = goalWeight ? parseInt(goalWeight) : (currentWeight ? parseInt(currentWeight) * 0.85 : 150);
    const proteinTarget = Math.round(weight * 0.8);

    // Generate 7-day plan
    const days = DAYS.map((day, idx) => {
      const breakfast = breakfasts[idx % breakfasts.length];
      const lunch = lunches[idx % lunches.length];
      const dinner = dinners[idx % dinners.length];
      const snack = snacks[idx % snacks.length];

      const totalProtein = breakfast.protein + lunch.protein + dinner.protein + snack.protein;
      const totalCalories = breakfast.calories + lunch.calories + dinner.calories + snack.calories;

      return {
        day,
        meals: { breakfast, lunch, dinner, snack },
        totalProtein,
        totalCalories
      };
    });

    // Generate grocery list
    const groceryList = generateGroceryList(days);

    return {
      days,
      proteinTarget,
      groceryList,
      challengeNote: CHALLENGE_NOTES[challenge] || null,
      prepTips: MEAL_PREP_TIPS[challenge] || MEAL_PREP_TIPS.consistency
    };
  };

  const generateGroceryList = (days) => {
    const items = {
      proteins: new Set(),
      vegetables: new Set(),
      fruits: new Set(),
      dairy: new Set(),
      pantry: new Set()
    };

    // Extract ingredients from meal names (simplified approach)
    days.forEach(day => {
      Object.values(day.meals).forEach(meal => {
        const name = meal.name.toLowerCase();
        
        // Proteins
        if (name.includes('chicken')) items.proteins.add('Chicken breast or rotisserie chicken');
        if (name.includes('turkey')) items.proteins.add('Ground turkey or deli turkey');
        if (name.includes('salmon')) items.proteins.add('Salmon filets');
        if (name.includes('tuna')) items.proteins.add('Canned tuna');
        if (name.includes('shrimp')) items.proteins.add('Shrimp (fresh or frozen)');
        if (name.includes('cod') || name.includes('tilapia') || name.includes('fish')) items.proteins.add('White fish filets');
        if (name.includes('beef') || name.includes('steak')) items.proteins.add('Lean beef');
        if (name.includes('egg')) items.proteins.add('Eggs (1-2 dozen)');
        if (name.includes('tofu')) items.proteins.add('Tofu');

        // Vegetables
        if (name.includes('spinach')) items.vegetables.add('Baby spinach');
        if (name.includes('broccoli')) items.vegetables.add('Broccoli');
        if (name.includes('asparagus')) items.vegetables.add('Asparagus');
        if (name.includes('zucchini')) items.vegetables.add('Zucchini');
        if (name.includes('brussels')) items.vegetables.add('Brussels sprouts');
        if (name.includes('cucumber')) items.vegetables.add('Cucumbers');
        if (name.includes('tomato')) items.vegetables.add('Cherry tomatoes');
        if (name.includes('bell pepper')) items.vegetables.add('Bell peppers');
        if (name.includes('mushroom')) items.vegetables.add('Mushrooms');
        if (name.includes('salad') || name.includes('greens')) items.vegetables.add('Mixed salad greens');
        if (name.includes('carrot')) items.vegetables.add('Carrots');

        // Fruits
        if (name.includes('banana')) items.fruits.add('Bananas');
        if (name.includes('berries') || name.includes('berry')) items.fruits.add('Mixed berries');
        if (name.includes('apple')) items.fruits.add('Apples');
        if (name.includes('avocado')) items.fruits.add('Avocados');
        if (name.includes('lemon')) items.fruits.add('Lemons');

        // Dairy
        if (name.includes('greek yogurt') || name.includes('yogurt')) items.dairy.add('Greek yogurt (plain)');
        if (name.includes('cottage cheese')) items.dairy.add('Cottage cheese');
        if (name.includes('feta')) items.dairy.add('Feta cheese');
        if (name.includes('cheese') && !name.includes('cottage') && !name.includes('feta')) items.dairy.add('Shredded cheese or string cheese');
        if (name.includes('cream cheese')) items.dairy.add('Cream cheese');
        if (name.includes('almond milk')) items.dairy.add('Almond milk (unsweetened)');

        // Pantry
        if (name.includes('oat')) items.pantry.add('Rolled oats');
        if (name.includes('quinoa')) items.pantry.add('Quinoa');
        if (name.includes('rice') && !name.includes('cauliflower')) items.pantry.add('Rice (brown or white)');
        if (name.includes('sweet potato')) items.pantry.add('Sweet potatoes');
        if (name.includes('protein powder') || name.includes('protein shake')) items.pantry.add('Protein powder');
        if (name.includes('almond butter') || name.includes('peanut butter') || name.includes('pb')) items.pantry.add('Nut butter');
        if (name.includes('olive oil')) items.pantry.add('Olive oil');
        if (name.includes('hummus')) items.pantry.add('Hummus');
        if (name.includes('crackers')) items.pantry.add('Whole grain crackers');
        if (name.includes('bread') || name.includes('toast')) items.pantry.add('Whole grain bread');
        if (name.includes('tortilla')) items.pantry.add('Low-carb tortillas');
        if (name.includes('honey')) items.pantry.add('Honey');
        if (name.includes('chia')) items.pantry.add('Chia seeds');
        if (name.includes('almonds') || name.includes('nuts')) items.pantry.add('Almonds or mixed nuts');
      });
    });

    return {
      proteins: Array.from(items.proteins),
      vegetables: Array.from(items.vegetables),
      fruits: Array.from(items.fruits),
      dairy: Array.from(items.dairy),
      pantry: Array.from(items.pantry)
    };
  };

  const handleCopyPlan = () => {
    if (!mealPlan) return;
    
    let planText = `MY 7-DAY MEAL PLAN\n`;
    planText += `Daily Protein Target: ${mealPlan.proteinTarget}g\n\n`;
    
    mealPlan.days.forEach(day => {
      planText += `${day.day.toUpperCase()} (${day.totalProtein}g protein)\n`;
      planText += `• Breakfast: ${day.meals.breakfast.name}\n`;
      planText += `• Lunch: ${day.meals.lunch.name}\n`;
      planText += `• Dinner: ${day.meals.dinner.name}\n`;
      planText += `• Snack: ${day.meals.snack.name}\n\n`;
    });

    planText += `GROCERY LIST\n`;
    planText += `Proteins: ${mealPlan.groceryList.proteins.join(', ')}\n`;
    planText += `Vegetables: ${mealPlan.groceryList.vegetables.join(', ')}\n`;
    planText += `Fruits: ${mealPlan.groceryList.fruits.join(', ')}\n`;
    planText += `Dairy: ${mealPlan.groceryList.dairy.join(', ')}\n`;
    planText += `Pantry: ${mealPlan.groceryList.pantry.join(', ')}\n`;

    navigator.clipboard.writeText(planText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    trackEvent('meal_plan_copied');
  };

  const handleShare = async () => {
    if (!mealPlan) return;
    
    const shareData = {
      title: 'My 7-Day Meal Plan',
      text: `Check out my personalized meal plan! Daily protein target: ${mealPlan.proteinTarget}g`,
      url: window.location.href
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        trackEvent('meal_plan_shared');
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      handleCopyPlan();
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!email) return;
    
    // In a real implementation, this would send to a backend
    trackEvent('meal_plan_email_requested', { email });
    setEmailSent(true);
    setTimeout(() => setEmailSent(false), 3000);
  };

  const toggleDay = (day) => {
    setExpandedDays(prev => ({ ...prev, [day]: !prev[day] }));
  };

  const isFormComplete = medication && diet && challenge && activity;

  if (showPlan && mealPlan) {
    return (
      <div className="min-h-screen bg-[#0B1020] text-white pb-32">
        <SEOHead 
          title="Your Personalized Meal Plan | Weight Loss Care Team"
          description="Your custom 7-day meal plan optimized for GLP-1 medication success."
          path="/meal-plan"
        />

        {/* Print styles */}
        <style>{`
          @media print {
            body { background: white !important; color: black !important; }
            .no-print { display: none !important; }
            .print-section { break-inside: avoid; margin-bottom: 20px; }
          }
        `}</style>

        <div className="bg-gradient-to-b from-[#1E293B] to-[#0B1020] pt-8 pb-12 px-4 no-print">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">Your 7-Day Meal Plan</h1>
            <p className="text-[#94A3B8]">Customized for your goals and preferences</p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 -mt-4">
          {/* Protein Target Banner */}
          <Card className="bg-gradient-to-r from-[#22C55E]/20 to-[#3B82F6]/20 border-[#22C55E]/30 p-4 mb-6">
            <div className="text-center">
              <p className="text-[#94A3B8] text-sm mb-1">Your Daily Protein Target</p>
              <p className="text-3xl font-bold text-[#22C55E]">{mealPlan.proteinTarget}g</p>
              <p className="text-[#94A3B8] text-xs mt-1">We've built this plan to hit that target each day</p>
            </div>
          </Card>

          {/* Challenge Note */}
          {mealPlan.challengeNote && (
            <Card className="bg-[#F59E0B]/10 border-[#F59E0B]/30 p-4 mb-6">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                <p className="text-[#F8FAFC] text-sm">{mealPlan.challengeNote}</p>
              </div>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 mb-6 no-print">
            <Button 
              onClick={regeneratePlan}
              className="flex-1 bg-[#3B82F6] hover:bg-[#2563EB] text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Generate New Week
            </Button>
            <Button 
              onClick={() => window.print()}
              variant="outline"
              className="border-[#334155] text-white hover:bg-[#1E293B]"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button 
              onClick={() => { setShowPlan(false); setMealPlan(null); }}
              variant="outline"
              className="border-[#334155] text-white hover:bg-[#1E293B]"
            >
              Adjust
            </Button>
          </div>

          {/* Meal Plan Grid */}
          <div className="space-y-3 print-section mb-8">
            {mealPlan.days.map((dayPlan) => (
              <Card key={dayPlan.day} className="bg-[#1E293B] border-[#334155] overflow-hidden">
                <button 
                  onClick={() => toggleDay(dayPlan.day)}
                  className="w-full flex items-center justify-between p-4 hover:bg-[#334155]/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-semibold">{dayPlan.day}</span>
                    <span className="text-sm text-green-400 bg-green-400/10 px-2 py-1 rounded">
                      {dayPlan.totalProtein}g protein
                    </span>
                    <span className="text-sm text-[#94A3B8] bg-[#0B1020] px-2 py-1 rounded">
                      {dayPlan.totalCalories} cal
                    </span>
                  </div>
                  {expandedDays[dayPlan.day] ? 
                    <ChevronUp className="w-5 h-5 text-[#94A3B8]" /> : 
                    <ChevronDown className="w-5 h-5 text-[#94A3B8]" />
                  }
                </button>
                
                {expandedDays[dayPlan.day] && (
                  <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="bg-[#0B1020] rounded-lg p-3">
                      <p className="text-xs text-amber-400 font-medium mb-1">Breakfast</p>
                      <p className="text-sm text-[#E2E8F0]">{dayPlan.meals.breakfast.name}</p>
                      <p className="text-xs text-[#94A3B8] mt-1">{dayPlan.meals.breakfast.protein}g protein • {dayPlan.meals.breakfast.calories} cal</p>
                    </div>
                    <div className="bg-[#0B1020] rounded-lg p-3">
                      <p className="text-xs text-blue-400 font-medium mb-1">Lunch</p>
                      <p className="text-sm text-[#E2E8F0]">{dayPlan.meals.lunch.name}</p>
                      <p className="text-xs text-[#94A3B8] mt-1">{dayPlan.meals.lunch.protein}g protein • {dayPlan.meals.lunch.calories} cal</p>
                    </div>
                    <div className="bg-[#0B1020] rounded-lg p-3">
                      <p className="text-xs text-purple-400 font-medium mb-1">Dinner</p>
                      <p className="text-sm text-[#E2E8F0]">{dayPlan.meals.dinner.name}</p>
                      <p className="text-xs text-[#94A3B8] mt-1">{dayPlan.meals.dinner.protein}g protein • {dayPlan.meals.dinner.calories} cal</p>
                    </div>
                    <div className="bg-[#0B1020] rounded-lg p-3">
                      <p className="text-xs text-green-400 font-medium mb-1">Snack</p>
                      <p className="text-sm text-[#E2E8F0]">{dayPlan.meals.snack.name}</p>
                      <p className="text-xs text-[#94A3B8] mt-1">{dayPlan.meals.snack.protein}g protein • {dayPlan.meals.snack.calories} cal</p>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>

          {/* Grocery List */}
          <Card className="bg-[#1E293B] border-[#334155] p-6 mb-6 print-section">
            <div className="flex items-center gap-2 mb-4">
              <ShoppingCart className="w-5 h-5 text-[#22C55E]" />
              <h2 className="text-lg font-bold text-[#F8FAFC]">Weekly Grocery List</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {mealPlan.groceryList.proteins.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-[#F59E0B] mb-2">Proteins</p>
                  <ul className="space-y-1">
                    {mealPlan.groceryList.proteins.map((item, i) => (
                      <li key={i} className="text-sm text-[#CBD5E1] flex items-start gap-2">
                        <span className="text-[#F59E0B]">•</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {mealPlan.groceryList.vegetables.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-[#22C55E] mb-2">Vegetables</p>
                  <ul className="space-y-1">
                    {mealPlan.groceryList.vegetables.map((item, i) => (
                      <li key={i} className="text-sm text-[#CBD5E1] flex items-start gap-2">
                        <span className="text-[#22C55E]">•</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {mealPlan.groceryList.fruits.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-[#EC4899] mb-2">Fruits</p>
                  <ul className="space-y-1">
                    {mealPlan.groceryList.fruits.map((item, i) => (
                      <li key={i} className="text-sm text-[#CBD5E1] flex items-start gap-2">
                        <span className="text-[#EC4899]">•</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {mealPlan.groceryList.dairy.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-[#3B82F6] mb-2">Dairy</p>
                  <ul className="space-y-1">
                    {mealPlan.groceryList.dairy.map((item, i) => (
                      <li key={i} className="text-sm text-[#CBD5E1] flex items-start gap-2">
                        <span className="text-[#3B82F6]">•</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {mealPlan.groceryList.pantry.length > 0 && (
                <div className="sm:col-span-2">
                  <p className="text-sm font-medium text-[#A855F7] mb-2">Pantry Staples</p>
                  <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1">
                    {mealPlan.groceryList.pantry.map((item, i) => (
                      <li key={i} className="text-sm text-[#CBD5E1] flex items-start gap-2">
                        <span className="text-[#A855F7]">•</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Card>

          {/* Meal Prep Tips */}
          <Card className="bg-[#1E293B] border-[#334155] p-6 mb-6 print-section">
            <div className="flex items-center gap-2 mb-4">
              <Lightbulb className="w-5 h-5 text-[#F59E0B]" />
              <h2 className="text-lg font-bold text-[#F8FAFC]">Meal Prep Tips</h2>
            </div>
            <ul className="space-y-3">
              {mealPlan.prepTips.map((tip, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#F59E0B]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#F59E0B] text-xs font-bold">{i + 1}</span>
                  </div>
                  <p className="text-sm text-[#CBD5E1]">{tip}</p>
                </li>
              ))}
            </ul>
          </Card>

          {/* Share & Save Section */}
          <Card className="bg-[#1E293B] border-[#334155] p-6 mb-6 no-print">
            <h2 className="text-lg font-bold text-[#F8FAFC] mb-4">Save Your Plan</h2>
            <div className="flex flex-wrap gap-3 mb-6">
              <Button 
                onClick={handleCopyPlan}
                variant="outline"
                className="flex-1 border-[#334155] text-white hover:bg-[#334155]"
              >
                {copied ? <Check className="w-4 h-4 mr-2 text-green-400" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copied!' : 'Copy Plan'}
              </Button>
              <Button 
                onClick={handleShare}
                variant="outline"
                className="flex-1 border-[#334155] text-white hover:bg-[#334155]"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share Plan
              </Button>
            </div>

            {/* Email Capture */}
            <div className="border-t border-[#334155] pt-4">
              <p className="text-sm text-[#94A3B8] mb-3">
                Want this in your inbox so you can reference it all week? Drop your email and we'll send it over.
              </p>
              <form onSubmit={handleEmailSubmit} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 bg-[#0B1020] border border-[#334155] rounded-lg px-4 py-2 text-white placeholder-[#64748B] focus:outline-none focus:border-[#3B82F6]"
                />
                <Button 
                  type="submit"
                  className="bg-[#3B82F6] hover:bg-[#2563EB]"
                  disabled={emailSent}
                >
                  {emailSent ? <Check className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                </Button>
              </form>
              {emailSent && (
                <p className="text-sm text-green-400 mt-2">We'll send your plan shortly!</p>
              )}
            </div>
          </Card>

          {/* Bottom Quote */}
          <Card className="bg-[#1E293B]/50 border-[#334155] p-6 mb-6 no-print">
            <p className="text-[#94A3B8] text-center italic">
              "Your nutrition is one piece. Your forecast is the other."
            </p>
          </Card>

          {/* CTA */}
          <Button
            onClick={() => navigate('/calculator')}
            className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white py-6 text-lg font-semibold rounded-xl no-print"
            data-testid="see-forecast-cta"
          >
            See Your 90-Day Forecast
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>

          {/* Cross-link to Strength Plan Builder */}
          <button
            onClick={() => navigate('/strength-plan')}
            className="w-full mt-3 flex items-center justify-center gap-2 text-[#A855F7] hover:text-[#C084FC] font-medium text-sm no-print"
            data-testid="meal-to-strength-link"
          >
            <Dumbbell className="w-4 h-4" />
            Build a matching workout plan
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1020] text-white pb-32">
      <SEOHead 
        title="Personalized Meal Plan Generator | Weight Loss Care Team"
        description="Generate a custom 7-day meal plan optimized for your GLP-1 medication and dietary preferences."
        path="/meal-plan"
      />

      {/* Header */}
      <div className="bg-gradient-to-b from-[#1E293B] to-[#0B1020] pt-8 pb-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <Link to="/" className="text-[#94A3B8] hover:text-white text-sm mb-4 inline-block">
            ← Back to Home
          </Link>
          <div className="flex items-center justify-center gap-2 mb-4">
            <UtensilsCrossed className="w-8 h-8 text-[#F59E0B]" />
            <h1 className="text-2xl sm:text-3xl font-bold">Meal Plan Generator</h1>
          </div>
          <p className="text-[#94A3B8] text-base sm:text-lg">
            Get a personalized 7-day meal plan with grocery list
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-4">
        <Card className="bg-[#1E293B] border-[#334155] p-6">
          {/* Weight Inputs */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-[#94A3B8] mb-2">Current Weight (lbs)</label>
              <input
                type="number"
                value={currentWeight}
                onChange={(e) => setCurrentWeight(e.target.value)}
                placeholder="e.g., 200"
                className="w-full bg-[#0B1020] border border-[#334155] rounded-lg px-4 py-3 text-white placeholder-[#64748B] focus:outline-none focus:border-[#3B82F6]"
                data-testid="current-weight"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#94A3B8] mb-2">Goal Weight (lbs)</label>
              <input
                type="number"
                value={goalWeight}
                onChange={(e) => setGoalWeight(e.target.value)}
                placeholder="e.g., 160"
                className="w-full bg-[#0B1020] border border-[#334155] rounded-lg px-4 py-3 text-white placeholder-[#64748B] focus:outline-none focus:border-[#3B82F6]"
                data-testid="goal-weight"
              />
            </div>
          </div>

          {/* Medication Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-[#94A3B8] mb-3">Current Medication</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'semaglutide', label: 'Semaglutide' },
                { value: 'tirzepatide', label: 'Tirzepatide' },
                { value: 'notstarted', label: 'Not Started' }
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setMedication(opt.value)}
                  className={`py-3 px-2 rounded-lg text-sm font-medium transition-all ${
                    medication === opt.value
                      ? 'bg-[#3B82F6] text-white'
                      : 'bg-[#0B1020] text-[#94A3B8] hover:bg-[#334155]'
                  }`}
                  data-testid={`med-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Dietary Preference */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-[#94A3B8] mb-3">Dietary Preference</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'standard', label: 'No Restrictions' },
                { value: 'lowcarb', label: 'Low Carb' },
                { value: 'highprotein', label: 'High Protein' },
                { value: 'vegetarian', label: 'Vegetarian' }
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setDiet(opt.value)}
                  className={`py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                    diet === opt.value
                      ? 'bg-[#3B82F6] text-white'
                      : 'bg-[#0B1020] text-[#94A3B8] hover:bg-[#334155]'
                  }`}
                  data-testid={`diet-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Biggest Challenge */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-[#94A3B8] mb-3">Biggest Challenge</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'nausea', label: 'Nausea' },
                { value: 'lowappetite', label: 'Low Appetite' },
                { value: 'lowenergy', label: 'Low Energy' },
                { value: 'consistency', label: 'Staying Consistent' }
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setChallenge(opt.value)}
                  className={`py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                    challenge === opt.value
                      ? 'bg-[#3B82F6] text-white'
                      : 'bg-[#0B1020] text-[#94A3B8] hover:bg-[#334155]'
                  }`}
                  data-testid={`challenge-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Activity Level */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-[#94A3B8] mb-3">Activity Level</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'sedentary', label: 'Sedentary' },
                { value: 'light', label: 'Lightly Active' },
                { value: 'moderate', label: 'Moderately Active' },
                { value: 'very', label: 'Very Active' }
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setActivity(opt.value)}
                  className={`py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                    activity === opt.value
                      ? 'bg-[#3B82F6] text-white'
                      : 'bg-[#0B1020] text-[#94A3B8] hover:bg-[#334155]'
                  }`}
                  data-testid={`activity-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Generate Button */}
          <Button
            onClick={generateMealPlan}
            disabled={!isFormComplete}
            className={`w-full py-6 text-lg font-semibold rounded-xl transition-all ${
              isFormComplete 
                ? 'bg-[#F59E0B] hover:bg-[#D97706] text-white' 
                : 'bg-[#334155] text-[#64748B] cursor-not-allowed'
            }`}
            data-testid="generate-plan-btn"
          >
            Generate My Meal Plan
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </Card>
      </div>
    </div>
  );
};

export default MealPlan;
